import { useState, useEffect, useCallback } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { Editor } from '@tinymce/tinymce-react';
import { PublishArticleModal } from './PublishArticleModal';

// TinyMCE type definitions
interface BlobInfo {
  blob: () => Blob;
  filename: () => string;
  base64: () => string;
}

interface TinyMCEEditor {
  getContent: () => string;
  setContent: (content: string, args?: { no_events?: boolean }) => void;
  on: (event: string, callback: (e: { content?: string }) => void) => void;
}

interface PastePostprocessArgs {
  node: HTMLElement;
}
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useGetArticleByIdQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useTranslateArticleMutation,
  useUploadImageMutation,
} from '@/services/ArticleService';
import { Article, ArticleTranslation } from '@/types/cms.types';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import clsx from 'clsx';
import * as Yup from 'yup';
import ArticlePreviewModal from './ArticlePreviewModal';
import { KeenIcon } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import MediaUpload from '../habor-management/MediaUpload';
import { Dispatch, SetStateAction } from 'react';

const articleSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  summary: Yup.string()
    .required('Summary is required')
    .max(500, 'Summary must not exceed 500 characters'),
  body: Yup.string().required('Body is required'),
  category: Yup.string().required('Category is required'),
  language: Yup.string().required('Language is required'),
  slug: Yup.string().required('Slug is required'),
});

const ArticleForm = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();
  // const intl = useIntl();

  const [formData, setFormData] = useState<Partial<Article>>({
    title: '',
    summary: '',
    body: '',
    category: '',
    language: 'en',
    status: 'draft',
    slug: '',
    coverImage_captions: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [translations, setTranslations] = useState<
    Record<string, ArticleTranslation>
  >({});
  const [formDataSnapshot, setFormDataSnapshot] = useState<Partial<Article>>(
    {}
  );
  const [translationsValid, setTranslationsValid] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState<
    'draft' | 'published' | null
  >(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);

  const { data: articleData, isLoading } = useGetArticleByIdQuery(
    id as string,
    {
      skip: !id,
    }
  );

  const [createArticle] = useCreateArticleMutation();
  const [updateArticle] = useUpdateArticleMutation();
  const [translateArticle] = useTranslateArticleMutation();
  const [uploadImage] = useUploadImageMutation();

  useEffect(() => {
    if (articleData) {
      setFormData({
        ...articleData,
        coverImage: articleData.coverImageUrl || null,
      });
      if (articleData.image_captions) {
        setFormData((prev) => ({
          ...prev,
          coverImage_captions: [
            {
              image_index: articleData.image_captions?.image_index || 0,
              captions: articleData.image_captions?.captions || {},
            },
          ],
        }));
      } else if (articleData.image_caption) {
        setFormData((prev) => ({
          ...prev,
          coverImage_captions: [
            {
              image_index: articleData.image_caption?.image_index || 0,
              captions: articleData.image_caption?.translations || {},
            },
          ],
        }));
      }

      // Seed translations from already-stored API data (edit mode) so the
      // preview modal shows them immediately without calling the translate API.
      if (
        articleData.translations &&
        typeof articleData.translations === 'object' &&
        Object.keys(articleData.translations).length > 0
      ) {
        setTranslations(articleData.translations);
        setTranslationsValid(true);
        setFormDataSnapshot({
          title: articleData.title,
          summary: articleData.summary,
          body: articleData.body,
        });
      }
    }
  }, [articleData]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    // Validate slug field - only allow alphanumeric and hyphens
    if (name === 'slug') {
      const sanitizedValue = value.replace(/[^a-zA-Z0-9-]/g, '');
      setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleTranslationsGenerated = useCallback(
    (generatedTranslations: Record<string, ArticleTranslation>) => {
      setTranslations(generatedTranslations);
      // Take snapshot of form data when translations are generated
      setFormDataSnapshot({
        title: formData.title,
        summary: formData.summary,
        body: formData.body,
      });
      setTranslationsValid(true);
    },
    [formData.title, formData.summary, formData.body]
  );

  // Detect form changes and invalidate translations
  useEffect(() => {
    if (translationsValid) {
      const hasChanged =
        formData.title !== formDataSnapshot.title ||
        formData.summary !== formDataSnapshot.summary ||
        formData.body !== formDataSnapshot.body;

      if (hasChanged) {
        setTranslationsValid(false);
      }
    }
  }, [
    formData.title,
    formData.summary,
    formData.body,
    formDataSnapshot,
    translationsValid,
  ]);

  const handleSubmit = async (
    status: 'draft' | 'published',
    publishedAt?: string | null
  ) => {
    setSubmittingStatus(status);
    try {
      await articleSchema.validate(formData, { abortEarly: false });

      // Only auto-translate on submit when there are no translations at all.
      // If translations were seeded from the API (edit mode) or generated via
      // the preview modal, use them as-is — don't call the API again.
      let translationsToUse = translations;
      if (Object.keys(translationsToUse).length === 0) {
        const translationPayload = {
          title: formData.title,
          summary: formData.summary,
          body: formData.body,
          source_language: formData.language || 'en',
        };
        try {
          const translationResult =
            await translateArticle(translationPayload).unwrap();
          if (translationResult && translationResult.translations) {
            translationsToUse = translationResult.translations;
            setTranslations(translationsToUse);
            setFormDataSnapshot({
              title: formData.title,
              summary: formData.summary,
              body: formData.body,
            });
            setTranslationsValid(true);
          }
        } catch {
          enqueueSnackbar('Failed to generate translations', {
            variant: 'error',
          });
        }
      }

      const body = new FormData();
      body.append('title', formData.title || '');
      body.append('summary', formData.summary || '');
      body.append('body', formData.body || '');
      body.append('category', formData.category || '');
      body.append('language', formData.language || 'en');
      body.append('status', status);
      body.append('slug', formData.slug || '');

      if (formData.coverImage instanceof File) {
        body.append('coverImage', formData.coverImage);
        if (
          formData.coverImage_captions &&
          formData.coverImage_captions.length > 0
        ) {
          body.append(
            'image_captions',
            JSON.stringify(formData.coverImage_captions[0])
          );
        }
      } else if (formData.coverImage === null) {
        // Cover image was explicitly removed — tell the backend to clear it
        body.append('coverImage', 'remove');
        body.append('image_captions', 'remove');
      } else if (
        formData.coverImage_captions &&
        formData.coverImage_captions.length > 0
      ) {
        // Existing URL image (no change), just update captions if present
        body.append(
          'image_captions',
          JSON.stringify(formData.coverImage_captions[0])
        );
      }

      // Add publishedAt if publishing with scheduled time
      if (status === 'published' && publishedAt) {
        body.append('publishedAt', publishedAt);
      }

      // Add translations if available
      if (Object.keys(translationsToUse).length > 0) {
        body.append('translations', JSON.stringify(translationsToUse));
      }

      if (id) {
        await updateArticle({ id, body }).unwrap();
        enqueueSnackbar('Article updated successfully', { variant: 'success' });
      } else {
        await createArticle(body).unwrap();
        enqueueSnackbar('Article created successfully', { variant: 'success' });
      }
      navigate('/article-management');
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) newErrors[error.path] = error.message;
        });
        setErrors(newErrors);
      } else {
        const error = err as Record<string, unknown>;
        let backendMessage: string | undefined;

        if (
          error?.data &&
          typeof error.data === 'object' &&
          error.data !== null
        ) {
          const data = error.data as Record<string, unknown>;

          if (typeof data.message === 'string') {
            backendMessage = data.message;
          } else if (data.errors) {
            const errors = data.errors as unknown;

            if (Array.isArray(errors)) {
              const firstString = errors.find((v) => typeof v === 'string');
              if (typeof firstString === 'string') {
                backendMessage = firstString;
              } else {
                const firstObj = errors.find(
                  (v) => typeof v === 'object' && v !== null
                ) as Record<string, unknown> | undefined;

                if (firstObj) {
                  const constraints = firstObj.constraints as unknown;
                  if (typeof constraints === 'object' && constraints !== null) {
                    const constraintMessages = Object.values(
                      constraints as Record<string, unknown>
                    ).filter((v): v is string => typeof v === 'string');

                    if (constraintMessages.length > 0) {
                      backendMessage = constraintMessages.join('\n');
                    }
                  }
                }
              }
            } else if (typeof errors === 'object' && errors !== null) {
              const messages = Object.values(errors as Record<string, unknown>)
                .flatMap((v) => (Array.isArray(v) ? v : [v]))
                .filter((v): v is string => typeof v === 'string');
              if (messages.length > 0) backendMessage = messages.join('\n');
            }
          }
        }

        if (!backendMessage && typeof error?.message === 'string') {
          backendMessage = error.message;
        }

        enqueueSnackbar(backendMessage || 'Failed to save article', {
          variant: 'error',
        });
      }
    } finally {
      setSubmittingStatus(null);
    }
  };

  if (isLoading && id) {
    return <div className="p-10 text-center">Loading article...</div>;
  }

  return (
    <section role="region" aria-label="Article form">
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle
                text={id ? 'Edit Article' : 'Add New Article'}
              />
              <ToolbarDescription>
                {id
                  ? 'Update your article details'
                  : 'Create a new article for your CMS'}
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                className="btn btn-sm btn-light h-10"
                onClick={() => navigate('/article-management')}
                disabled={submittingStatus !== null}
              >
                Cancel
              </button>
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card p-4">
          <div className="card-body">
            <div className="flex flex-col gap-6 max-w-5xl mx-auto">
              <div className="space-y-4">
                <MediaUpload<Article>
                  field="coverImage"
                  media={formData.coverImage}
                  media_captions={formData.coverImage_captions}
                  setFormData={
                    setFormData as Dispatch<SetStateAction<Partial<Article>>>
                  }
                  errors={errors}
                  setErrors={
                    setErrors as Dispatch<
                      SetStateAction<{ [key: string]: string }>
                    >
                  }
                  disabled={submittingStatus !== null}
                  allowedTypes={['image/jpeg', 'image/jpg', 'image/png']}
                  maxFiles={1}
                  previewMode={true}
                  guidelinesResolution="Recommended resolution: 1200x500 px"
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="language" className="form-label">
                      Language
                    </label>
                    <select
                      id="language"
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="select select-bordered w-full"
                      aria-label="Language for the article"
                    >
                      <option value="en">English</option>
                      <option value="fi">Finnish</option>
                      <option value="sv">Swedish</option>
                      <option value="de">German</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="category" className="form-label">
                      Category
                    </label>
                    <input
                      id="category"
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={clsx(
                        'input input-bordered w-full',
                        errors.category &&
                          'border-danger contrast:border-red-500 '
                      )}
                      aria-label="Category for the article"
                      placeholder="e.g. Safety, News"
                      autoComplete="off"
                    />
                    {errors.category && (
                      <span className="text-danger contrast:text-red-500 text-xs">
                        {errors.category}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="title" className="form-label">
                    Title
                  </label>
                  <input
                    id="title"
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className={clsx(
                      'input input-bordered w-full',
                      errors.title && 'border-danger contrast:border-red-500 '
                    )}
                    placeholder="Enter article title"
                    aria-label="Article title"
                    autoComplete="off"
                  />
                  {errors.title && (
                    <span className="text-danger contrast:text-red-500 text-xs">
                      {errors.title}
                    </span>
                  )}
                </div>

                <div>
                  <label htmlFor="slug" className="form-label">
                    Slug
                  </label>
                  <input
                    id="slug"
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    className={clsx(
                      'input input-bordered w-full',
                      errors.slug && 'border-danger contrast:border-red-500 '
                    )}
                    placeholder="article-url-slug"
                    aria-label="Slug for the article"
                    autoComplete="off"
                  />
                  {errors.slug && (
                    <span className="text-danger contrast:text-red-500 text-xs">
                      {errors.slug}
                    </span>
                  )}
                </div>

                <div>
                  <label htmlFor="summary" className="form-label">
                    Summary
                  </label>
                  <textarea
                    id="summary"
                    name="summary"
                    value={formData.summary}
                    onChange={handleInputChange}
                    className={clsx(
                      'textarea textarea-bordered w-full h-32',
                      errors.summary && 'border-danger contrast:border-red-500 '
                    )}
                    maxLength={500}
                    placeholder="Brief summary of the article"
                    aria-label="Brief summary of the article"
                    autoComplete="off"
                  />
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-500">
                      {(formData.summary || '').length}/500 characters
                    </span>
                  </div>
                  {errors.summary && (
                    <span className="text-danger contrast:text-red-500 text-xs">
                      {errors.summary}
                    </span>
                  )}
                </div>

                <div>
                  <label htmlFor="body" className="form-label">
                    Body Content
                  </label>
                  <Editor
                    id="body"
                    tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
                    value={formData.body}
                    aria-label="Body content"
                    init={{
                      ...tinymceInit,
                      height: 800,
                      iframe_attrs: {
                        title: 'Body content editor',
                      },
                      images_file_types: 'jpg,jpeg,png',
                      toolbar: tinymceInit.toolbar.concat('image'),
                      images_upload_handler: (blobInfo: BlobInfo) =>
                        new Promise((resolve, reject) => {
                          const file = blobInfo.blob();
                          const allowedTypes = [
                            'image/jpeg',
                            'image/jpg',
                            'image/png',
                          ];

                          if (!allowedTypes.includes(file.type)) {
                            reject('Only JPG, JPEG and PNG images are allowed');
                            return;
                          }

                          const formData = new FormData();
                          // Upload as binary file
                          formData.append('image', file, blobInfo.filename());

                          uploadImage(formData)
                            .unwrap()
                            .then((response) => {
                              resolve(response.url);
                            })
                            .catch((error) => {
                              console.error('Image upload failed:', error);
                              reject('Failed to upload image');
                            });
                        }),
                      paste_postprocess: (
                        editor: TinyMCEEditor,
                        args: PastePostprocessArgs
                      ) => {
                        const BACKEND_URL = 'https://minioapi.saaristo.online';

                        // Find all img elements in the pasted content
                        const images = args.node.querySelectorAll('img');

                        images.forEach((img: Element) => {
                          const src = img.getAttribute('src');

                          // Check if it's an external URL (not from our backend)
                          if (
                            src &&
                            (src.startsWith('http://') ||
                              src.startsWith('https://')) &&
                            !src.startsWith(BACKEND_URL)
                          ) {
                            // Upload the external URL to our backend
                            const formData = new FormData();
                            formData.append('url', src);

                            uploadImage(formData)
                              .unwrap()
                              .then((response) => {
                                // Use setTimeout to update content after paste completes
                                setTimeout(() => {
                                  const content = editor.getContent();

                                  // TinyMCE encodes & as &amp; in HTML
                                  const encodedSrc = src.replace(/&/g, '&amp;');

                                  // Replace the external URL with our uploaded URL
                                  const updatedContent = content.replace(
                                    new RegExp(
                                      `src="${encodedSrc.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`,
                                      'g'
                                    ),
                                    `src="${response.url}"`
                                  );

                                  if (content !== updatedContent) {
                                    editor.setContent(updatedContent);
                                  }
                                }, 100);

                                enqueueSnackbar(
                                  'External image uploaded successfully',
                                  { variant: 'success' }
                                );
                              })
                              .catch((error) => {
                                console.error(
                                  'Failed to upload external image:',
                                  error
                                );
                                enqueueSnackbar(
                                  `Failed to upload image from ${src}`,
                                  { variant: 'error' }
                                );
                              });
                          }
                        });
                      },
                      setup: (editor: TinyMCEEditor) => {
                        const existingSetup = (
                          tinymceInit as unknown as {
                            setup?: (ed: TinyMCEEditor) => void;
                          }
                        ).setup;

                        if (typeof existingSetup === 'function') {
                          existingSetup(editor);
                        }

                        editor.on('init', () => {
                          const iframe = document.getElementById(
                            'body_ifr'
                          ) as HTMLIFrameElement | null;

                          if (iframe) {
                            iframe.setAttribute('title', 'Body content editor');
                            iframe.setAttribute(
                              'aria-label',
                              'Body content editor'
                            );
                          }
                        });

                        // Track URLs being uploaded to prevent duplicates
                        const uploadingUrls = new Set<string>();
                        const uploadedUrls = new Map<string, string>();
                        const BACKEND_URL = 'https://minioapi.saaristo.online';
                        let isUpdating = false; // Flag to prevent infinite loop

                        // Intercept when images are inserted via the image dialog
                        editor.on('BeforeSetContent', (e) => {
                          // Skip processing if we're updating from our own upload
                          if (isUpdating) {
                            return;
                          }

                          // Check if content contains img tags with external URLs
                          if (e.content && e.content.includes('<img')) {
                            const tempDiv = document.createElement('div');
                            tempDiv.innerHTML = e.content;
                            const images = tempDiv.querySelectorAll('img');

                            images.forEach((img: Element) => {
                              const src = img.getAttribute('src');

                              // Check if it's an external URL (not from our backend) and not already uploaded
                              if (
                                src &&
                                (src.startsWith('http://') ||
                                  src.startsWith('https://')) &&
                                !src.startsWith(BACKEND_URL) &&
                                !uploadingUrls.has(src) &&
                                !uploadedUrls.has(src)
                              ) {
                                // Mark as uploading
                                uploadingUrls.add(src);

                                // Upload the external URL to our backend
                                const formData = new FormData();
                                formData.append('url', src);

                                uploadImage(formData)
                                  .unwrap()
                                  .then((response) => {
                                    // Store the mapping
                                    uploadedUrls.set(src, response.url);
                                    uploadingUrls.delete(src);

                                    // Use setTimeout to update content after the current event completes
                                    setTimeout(() => {
                                      const content = editor.getContent();

                                      // TinyMCE encodes & as &amp; in HTML, so we need to match that
                                      const encodedSrc = src.replace(
                                        /&/g,
                                        '&amp;'
                                      );

                                      // Replace all instances of the external URL (handle both single and double quotes)
                                      let updatedContent = content;

                                      // Replace with double quotes (using HTML-encoded URL)
                                      updatedContent = updatedContent.replace(
                                        new RegExp(
                                          `src="${encodedSrc.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}"`,
                                          'g'
                                        ),
                                        `src="${response.url}"`
                                      );

                                      // Replace with single quotes (using HTML-encoded URL)
                                      updatedContent = updatedContent.replace(
                                        new RegExp(
                                          `src='${encodedSrc.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}'`,
                                          'g'
                                        ),
                                        `src="${response.url}"`
                                      );

                                      if (content !== updatedContent) {
                                        isUpdating = true;
                                        editor.setContent(updatedContent);
                                        isUpdating = false;
                                      }
                                    }, 100);

                                    enqueueSnackbar(
                                      'External image uploaded successfully',
                                      { variant: 'success' }
                                    );
                                  })
                                  .catch((error) => {
                                    console.error(
                                      'Failed to upload external image:',
                                      error
                                    );
                                    uploadingUrls.delete(src);
                                    enqueueSnackbar(
                                      `Failed to upload image from ${src}`,
                                      { variant: 'error' }
                                    );
                                  });
                              } else if (src && uploadedUrls.has(src)) {
                                // If already uploaded, replace immediately
                                const uploadedUrl = uploadedUrls.get(src);
                                if (uploadedUrl) {
                                  img.setAttribute('src', uploadedUrl);
                                }
                              }
                            });

                            // Update the content with any replaced URLs
                            e.content = tempDiv.innerHTML;
                          }
                        });
                      },
                    }}
                    onEditorChange={(content: string) => {
                      setFormData((prev) => ({ ...prev, body: content }));
                      if (errors.body)
                        setErrors((prev) => ({ ...prev, body: '' }));
                    }}
                  />
                  {errors.body && (
                    <span className="text-danger contrast:text-red-500 text-xs">
                      {errors.body}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Preview Message - only show when form has content */}
          {formData.title && formData.summary && formData.body && (
            <div className="mt-6 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 border border-blue-200 dark:border-blue-800 contrast:border-blue-800 rounded-lg p-4 flex items-center justify-between gap-2">
              <div className="flex items-center">
                <KeenIcon
                  type="outline"
                  icon="paper-clip"
                  className="w-5 h-5 text-blue-600 dark:text-blue-100 contrast:text-blue-100 mr-2"
                />
                <p className="text-blue-700 dark:text-blue-100 contrast:text-blue-100 text-sm">
                  Preview translations before saving
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsPreviewModalOpen(true)}
                aria-label="Show preview"
                className="
                  btn btn-lg rounded-lg px-3 py-1
                  bg-blue-600 text-white
                  hover:bg-blue-700
                  focus:outline-none focus:ring-2 focus:ring-blue-500
                  contrast:bg-blue-700 contrast:hover:bg-blue-800 contrast:ring-offset-black
                "
              >
                Show Preview
              </button>
            </div>
          )}
        </div>
        {/* Form Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            className="btn btn-light"
            onClick={() => navigate('/article-management')}
            disabled={submittingStatus !== null}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleSubmit('draft')}
            disabled={
              submittingStatus !== null ||
              (id
                ? !hasPermission('article_management_update')
                : !hasPermission('article_management_create'))
            }
          >
            {submittingStatus === 'draft' ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={() => setPublishModalOpen(true)}
            disabled={
              submittingStatus !== null ||
              !hasPermission('article_management_publish')
            }
          >
            {submittingStatus === 'published' ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>

      <ArticlePreviewModal
        open={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        formData={formData}
        source_language={formData.language}
        initialTranslations={translations}
        onTranslationsGenerated={handleTranslationsGenerated}
        onSourceFormDataChange={(fields) =>
          setFormData((prev) => ({ ...prev, ...fields }))
        }
      />

      <PublishArticleModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        onConfirm={(publishedAt) => {
          setPublishModalOpen(false);
          // Pass publishedAt directly to handleSubmit
          handleSubmit('published', publishedAt);
        }}
      />
    </section>
  );
};

export { ArticleForm };
